/**
 * ELK layout engine integration for the journey builder.
 *
 * ⚠️  HERE BE DRAGONS ⚠️
 *
 * This file is the bridge between the Journey Builder's React Flow graph and
 * the ELK (Eclipse Layout Kernel) automatic layout algorithm. It handles:
 *
 *   - Converting React Flow nodes/edges into an ELK graph representation
 *   - Running the ELK layered layout algorithm
 *   - Applying ELK-computed positions back to React Flow nodes
 *   - Applying ELK-computed edge routing (bend points) to edges
 *   - Post-ELK symmetric convergence edge routing for branching layouts
 *   - Viewport bounds calculation for canvas sizing
 *
 * The layout pipeline is: convertToELKGraph → ELK.layout → symmetrize → apply.
 * ELK owns node positioning (it produces balanced, rank-aligned branch lanes);
 * branching nodes use ELK ports for per-branch edge routing, and a single
 * post-layout pass re-authors symmetric convergence (branch → join) edges.
 *
 * WHEN TO MODIFY THIS FILE:
 *   - Fixing layout bugs (nodes overlapping, edges misrouted, etc.)
 *   - Changing how the ELK graph is constructed (new node/edge types)
 *   - Adjusting the symmetrization pipeline for branching layouts
 *
 * WHEN NOT TO MODIFY THIS FILE:
 *   - Adding a new step type → update step-factory.ts and step-config.tsx
 *   - Changing graph mutation logic → update graph-mutations.ts
 *   - Changing layout constants → update elk-config.ts
 *   - Changing edge/node rendering → update the edge-types/ or node-types/ components
 */
import { ELKLayoutConfig, ELKLayoutResult, NodePosition, PortPosition, ViewportBounds, ElkNode, ElkExtendedEdge, ElkEdgeSection } from './elk-types'
import { JourneyNode, JourneyEdge, BranchEdgeData, JourneyStepData } from '../types/journey-nodes'
import { StepType } from '@pushly/models/lib/enums/journeys'
import { LAYOUT_CONSTANTS } from './elk-config'
import { BRANCH_HANDLE_PREFIX } from '../constants'
import { getNodeLayoutDimensions } from './node-dimensions'
import { isBranchingNode, getBranchingNodeData } from '../utils/graph/traversal'
import { getNodeOutStepId } from '../utils/graph/traversal'
import {
    buildSplitContext,
    computeBranchWidths,
    computeLaneCenters,
    clearSplitRegionEdgeRouting,
    updatePortPositions,
    computeConvergeContext,
    generateBranchEdgeSections,
    generateConvergenceEdgeSections,
    SplitContext,
} from './elk-symmetrize'
import ELK from 'elkjs/lib/elk.bundled.js'

const elk = new ELK()

/**
 * Sort nodes into variant-aware traversal order for ELK model ordering.
 *
 * ELK's forceNodeModelOrder places nodes left-to-right based on their
 * position in the children array. Without sorting, the exit node can
 * appear before variant path nodes, causing ELK to flip port order
 * to minimize crossings.
 *
 * Order: trigger → split → variant A path → variant B path → join/exit
 */
function sortNodesForModelOrder(nodes: JourneyNode[]): JourneyNode[] {
    const nodeMap = new Map(nodes.map(n => [n.id, n]))

    const ordered: JourneyNode[] = []
    const visited = new Set<string>()

    function visit(nodeId: string) {
        if (visited.has(nodeId)) return
        const node = nodeMap.get(nodeId)
        if (!node) return

        visited.add(nodeId)
        ordered.push(node)

        if (isBranchingNode(node)) {
            const branchingData = getBranchingNodeData(node)
            if (!branchingData) return
            const joinStepId = branchingData.join_step_id ? String(branchingData.join_step_id) : null

            // Walk each branch path in order, recursing into nested branching nodes
            for (const branch of branchingData.branches) {
                if (!branch.out_step_id) continue
                const outId = String(branch.out_step_id)
                if (outId === joinStepId) continue

                let currentId: string | null = outId
                while (currentId && currentId !== joinStepId) {
                    if (visited.has(currentId)) break
                    const currentNode = nodeMap.get(currentId)
                    if (!currentNode) break

                    // Recurse — handles nested branching nodes at any depth
                    visit(currentId)

                    // After visit, advance past the node. For branching nodes,
                    // visit() already walked all branches, so skip to join target.
                    if (isBranchingNode(currentNode)) {
                        const nestedData = getBranchingNodeData(currentNode)
                        currentId = nestedData?.join_step_id ? String(nestedData.join_step_id) : null
                    } else {
                        currentId = getNodeOutStepId(currentNode)
                    }
                }
            }

            // Visit join target last
            if (joinStepId) {
                visit(joinStepId)
            }
        } else {
            const nextId = getNodeOutStepId(node)
            if (nextId) {
                visit(nextId)
            }
        }
    }

    // Start from hidden nodes (global trigger)
    for (const node of nodes) {
        if (node.hidden) visit(node.id)
    }
    // Then trigger nodes
    for (const node of nodes) {
        if (node.data.step.type === StepType.TRIGGER) visit(node.id)
    }
    // Append any unvisited nodes
    for (const node of nodes) {
        if (!visited.has(node.id)) ordered.push(node)
    }

    return ordered
}

/**
 * Convert Journey nodes and edges to ELK graph format
 */
export function convertToELKGraph(
    nodes: JourneyNode[],
    edges: JourneyEdge[],
    config: ELKLayoutConfig,
): ElkNode {
    const sortedNodes = sortNodesForModelOrder(nodes)

    return {
        id: 'journey-root',
        layoutOptions: {
            'elk.algorithm': config.algorithm,
            'elk.direction': config.direction,
            'elk.edgeRouting': config.edgeRouting,
            'elk.spacing.nodeNode': `${config.nodeSeparation}`,
            'elk.layered.spacing.nodeNodeBetweenLayers': `${config.rankSeparation}`,
            'elk.spacing.edgeEdge': '40',
            'elk.layered.spacing.edgeEdgeBetweenLayers': '40',
            'elk.spacing.edgeNode': '30',
            'elk.layered.spacing.edgeNodeBetweenLayers': '30',
            'elk.layered.mergeEdges': 'false',
            'elk.layered.unnecessaryBendpoints': 'true',
            'elk.padding': `[top=${config.padding},left=${config.padding},bottom=${config.padding},right=${config.padding}]`,
            // BRANDES_KOEPF + BALANCED: symmetric branch fan-out + centered joins,
            // with same-layer nodes top-aligned by rank. This (plus ELK's native
            // ranking) lets ELK own node positioning; only symmetric convergence
            // edge routing is computed separately.
            'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
            'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
            // ASAP layering: rank each node by its LONGEST PATH FROM THE SOURCE,
            // so every variant's step-N sits exactly one rank below its parent
            // regardless of lane length. A 1-step lane's lone step then top-aligns
            // with multi-step lanes' first steps (a long convergence edge runs DOWN
            // to the join). NB: LONGEST_PATH_SOURCE (from source) ≠ LONGEST_PATH
            // (distance-to-sink) — the latter staggers same-rank cards. Default
            // NETWORK_SIMPLEX balances a short lane's lone step downward.
            'elk.layered.layering.strategy': 'LONGEST_PATH_SOURCE',
            'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
            'elk.layered.crossingMinimization.forceNodeModelOrder': 'true',
        },
        children: sortedNodes.map(node => {
            const { width, height } = getNodeLayoutDimensions(node)

            const elkNode: ElkNode = {
                id: node.id,
                width,
                height,
            }

            if (isBranchingNode(node)) {
                const branchingData = getBranchingNodeData(node)
                if (branchingData) {
                    const lastIdx = branchingData.branches.length - 1
                    elkNode.ports = branchingData.branches.map((branch, idx) => ({
                        id: `${BRANCH_HANDLE_PREFIX}${branch.key}`,
                        layoutOptions: {
                            'org.eclipse.elk.port.side': 'SOUTH',
                            // ELK orders SOUTH ports right-to-left for index 0→N,
                            // so we reverse the index to get left-to-right branch order.
                            'org.eclipse.elk.port.index': `${lastIdx - idx}`,
                        },
                    }))
                    elkNode.layoutOptions = {
                        'org.eclipse.elk.portConstraints': 'FIXED_ORDER',
                    }
                }
            }

            return elkNode
        }),
        edges: edges.map(edge => ({
            id: edge.id,
            sources: [edge.sourceHandle ? edge.sourceHandle : edge.source],
            targets: [edge.target],
        })) as ElkExtendedEdge[],
    }
}

/**
 * For purely linear graphs (no branching), align all visible nodes to a
 * common X centerline so the chain renders as a straight vertical line.
 *
 * ELK's layered algorithm can produce slight X offsets between layers
 * even when every layer contains a single node, because each layer's
 * bounding box is computed independently.
 */
function alignLinearGraph(
    nodes: JourneyNode[],
    positions: Map<string, NodePosition>,
    edgeRouting: Map<string, ElkEdgeSection[]>,
): void {
    const visibleNodes = nodes.filter(n => !n.hidden)

    // If any node is a branching node, skip — symmetrizeSplitLayout handles those.
    if (visibleNodes.some(n => isBranchingNode(n))) return

    // Find the centerline X of the first visible node (trigger) as anchor.
    let anchorCenterX: number | null = null
    for (const node of visibleNodes) {
        const pos = positions.get(node.id)
        if (pos) {
            const { width } = getNodeLayoutDimensions(node)
            anchorCenterX = pos.x + width / 2
            break
        }
    }

    if (anchorCenterX === null) return

    // Shift every node so its center aligns with the anchor centerline.
    for (const node of visibleNodes) {
        const pos = positions.get(node.id)
        if (!pos) continue
        const { width } = getNodeLayoutDimensions(node)
        pos.x = anchorCenterX - width / 2
    }

    // Clear ELK edge routing so edges fall back to React Flow's
    // getSmoothStepPath, which uses the corrected node positions.
    // For a straight vertical chain this produces clean vertical edges.
    edgeRouting.clear()
}

/**
 * Execute ELK layout and return positioned nodes with edge routing
 */
export async function calculateELKLayout(
    nodes: JourneyNode[],
    edges: JourneyEdge[],
    config: ELKLayoutConfig,
): Promise<ELKLayoutResult> {
    const elkGraph = convertToELKGraph(nodes, edges, config)

    const layoutedGraph = await elk.layout(elkGraph)

    const positions = new Map<string, NodePosition>()
    for (const node of layoutedGraph.children ?? []) {
        if (node.x !== undefined && node.y !== undefined) {
            positions.set(node.id, { x: node.x, y: node.y })
        }
    }

    const edgeRouting = new Map<string, ElkEdgeSection[]>()
    for (const edge of layoutedGraph.edges ?? []) {
        if (edge.sections && edge.sections.length > 0) {
            edgeRouting.set(edge.id, edge.sections as ElkEdgeSection[])
        }
    }

    // Extract ELK-computed port positions for split step nodes.
    // ELK's crossing minimization may reorder ports — we read the actual
    // positions so React Flow handles can match exactly.
    const portPositions = new Map<string, PortPosition[]>()
    for (const node of layoutedGraph.children ?? []) {
        if (node.ports && node.ports.length > 0) {
            portPositions.set(node.id, node.ports.map(p => ({
                portId: p.id,
                x: p.x ?? 0,
                y: p.y ?? 0,
            })))
        }
    }


    // Post-process: symmetric CONVERGENCE edge routing (Pass 2). ELK owns node
    // positioning + fan-out; this only authors mirror-symmetric merge edges, which
    // ELK's per-edge orthogonal router does not produce natively.
    symmetrizeSplitLayout(nodes, edges, positions, edgeRouting, portPositions)

    // Post-process: align all nodes to the same X centerline when the graph
    // is purely linear (no branching nodes). ELK's layered algorithm can
    // produce slight X offsets between layers even for a straight chain.
    alignLinearGraph(nodes, positions, edgeRouting)

    return { positions, edgeRouting, portPositions }
}

/**
 * Post-process ELK layout to author symmetric CONVERGENCE edge routing (Pass 2).
 *
 * ELK owns node positioning now (BRANDES_KOEPF / BALANCED placement +
 * LONGEST_PATH_SOURCE layering): ranks top-align, lanes fan out symmetrically,
 * and join targets center. The only residual is convergence (branch -> join)
 * edge routing — ELK's native per-edge orthogonal router produces ASYMMETRIC
 * merges (some edges take 3+ turns while siblings take 0), so we re-author those
 * sections here to be mirror-symmetric. Split edges and ports are regenerated in
 * the same pass so they stay consistent with the authored convergence geometry.
 *
 * Processed shallowest-first (ascending Y) so outer splits are routed before any
 * nested inner splits reference them.
 */
function symmetrizeSplitLayout(
    nodes: JourneyNode[],
    edges: JourneyEdge[],
    positions: Map<string, NodePosition>,
    edgeRouting: Map<string, ElkEdgeSection[]>,
    portPositions: Map<string, PortPosition[]>,
): void {
    const splitNodes = nodes.filter(n => isBranchingNode(n))

    const splitNodesByDepthAsc = [...splitNodes].sort((a, b) => {
        const posA = positions.get(a.id)
        const posB = positions.get(b.id)
        return (posA?.y ?? 0) - (posB?.y ?? 0)
    })

    for (const splitNode of splitNodesByDepthAsc) {
        const ctx = buildSplitContext(splitNode, nodes, positions)
        if (!ctx) continue
        symmetrizeSingleBranchEdgeRouting(ctx, edges, positions, edgeRouting, portPositions)
    }
}

/**
 * Pass 2: Generate edge routing for a single branching step.
 * Clears stale ELK routing, updates port positions, and generates
 * split + convergence edge sections using finalized positions.
 */
function symmetrizeSingleBranchEdgeRouting(
    ctx: SplitContext,
    edges: JourneyEdge[],
    positions: Map<string, NodePosition>,
    edgeRouting: Map<string, ElkEdgeSection[]>,
    portPositions: Map<string, PortPosition[]>,
): void {
    const { splitNode, splitPos, splitWidth, splitHeight, splitCenterX, joinStepId, branches, nodeMap, variantShaped } = ctx

    const branchHalfWidths = computeBranchWidths(branches, nodeMap, positions)
    const anyBranchHasNodes = branches.some(b => b.scopeNodeIds.length > 0)
    const laneCenters = computeLaneCenters(branches.length, splitCenterX, splitWidth, anyBranchHasNodes, branchHalfWidths)

    clearSplitRegionEdgeRouting(splitNode.id, joinStepId, branches, edges, edgeRouting)

    const ports = updatePortPositions(variantShaped, laneCenters, splitPos, splitWidth, splitHeight)
    portPositions.set(splitNode.id, ports)

    const convergeCtx = computeConvergeContext(branches, positions, nodeMap, joinStepId)

    generateBranchEdgeSections(branches, ports, splitNode, edges, positions, nodeMap, laneCenters, splitPos, convergeCtx, edgeRouting)
    generateConvergenceEdgeSections(branches, edges, positions, nodeMap, convergeCtx, joinStepId, edgeRouting)
}

/**
 * Apply ELK-computed positions to Journey nodes.
 * For split step nodes, also attaches ELK port positions to uiMeta
 * so React Flow handles can be positioned to match ELK's layout.
 */
export function applyELKLayout(
    nodes: JourneyNode[],
    positions: Map<string, NodePosition>,
    portPositions: Map<string, PortPosition[]>,
): JourneyNode[] {
    let minY = Infinity
    positions.forEach(pos => {
        minY = Math.min(minY, pos.y)
    })
    const yOffset = minY === Infinity ? 0 : minY

    return nodes.map(node => {
        const position = positions.get(node.id)
        if (!position) return node

        let updatedNode: JourneyNode = {
            ...node,
            position: {
                x: position.x,
                y: position.y - yOffset,
            },
        }

        // Attach ELK port positions to split step nodes so handles match ELK layout.
        // Construct a new JourneyStepData instead of mutating via setUIMeta().
        const ports = portPositions.get(node.id)
        if (ports && ports.length > 0) {
            const { width: nodeWidth } = getNodeLayoutDimensions(node)
            updatedNode = {
                ...updatedNode,
                data: new JourneyStepData({
                    step: node.data.step,
                    uiMeta: {
                        ...node.data.uiMeta,
                        elkPortPositions: ports.map(p => ({
                            portId: p.portId,
                            leftPercent: (p.x / nodeWidth) * 100,
                        })),
                    },
                }),
            }
        }

        return updatedNode
    })
}

/**
 * Apply ELK-computed edge routing to edges that have synthetic sections.
 * This includes split edges (split → variant target) and convergence edges
 * (last variant node → join target).
 */
export function applyELKEdgeRouting(
    edges: JourneyEdge[],
    edgeRouting: Map<string, ElkEdgeSection[]>,
    positions: Map<string, NodePosition>,
): JourneyEdge[] {
    let minY = Infinity
    positions.forEach(pos => {
        minY = Math.min(minY, pos.y)
    })
    const yOffset = minY === Infinity ? 0 : minY

    return edges.map(edge => {
        const sections = edgeRouting.get(edge.id)
        if (!sections || sections.length === 0) {
            // Clear stale routing when ELK does not return sections for this edge
            // (e.g., linear graph fallback after removing a split step).
            const edgeData = edge.data as (BranchEdgeData & { elkSections?: ElkEdgeSection[] }) | undefined
            if (!edgeData || !edgeData.elkSections) {
                return edge
            }

            const { elkSections, ...rest } = edgeData
            return {
                ...edge,
                data: Object.keys(rest).length > 0 ? rest : undefined,
            }
        }

        const normalizedSections = sections.map(section => ({
            ...section,
            startPoint: { x: section.startPoint.x, y: section.startPoint.y - yOffset },
            endPoint: { x: section.endPoint.x, y: section.endPoint.y - yOffset },
            bendPoints: section.bendPoints?.map(bp => ({ x: bp.x, y: bp.y - yOffset })),
        }))

        return {
            ...edge,
            data: {
                ...(edge.data as BranchEdgeData),
                elkSections: normalizedSections,
            },
        }
    })
}

/**
 * Calculate viewport bounds from positioned nodes
 */
export function calculateViewportBounds(
    nodes: JourneyNode[],
    padding: number = 40,
): ViewportBounds {
    if (nodes.length === 0) {
        return {
            width: Math.max(padding * 2, LAYOUT_CONSTANTS.MIN_VIEWPORT_WIDTH),
            height: Math.max(padding * 2, LAYOUT_CONSTANTS.MIN_VIEWPORT_HEIGHT),
            minX: 0, maxX: 0, minY: 0, maxY: 0,
        }
    }

    let minX = Infinity
    let maxX = -Infinity
    let minY = Infinity
    let maxY = -Infinity

    for (const node of nodes) {
        const x = node.position.x
        const y = node.position.y
        const { width, height } = getNodeLayoutDimensions(node)

        minX = Math.min(minX, x)
        maxX = Math.max(maxX, x + width)
        minY = Math.min(minY, y)
        maxY = Math.max(maxY, y + height)
    }

    const contentWidth = maxX - minX + padding * 2
    const contentHeight = maxY - minY + padding * 2
    const width = Math.max(contentWidth, LAYOUT_CONSTANTS.MIN_VIEWPORT_WIDTH)
    const height = Math.max(contentHeight, LAYOUT_CONSTANTS.MIN_VIEWPORT_HEIGHT)

    return {
        width, height,
        minX: minX - padding, maxX: maxX + padding,
        minY: minY - padding, maxY: maxY + padding,
    }
}
